// ── „Bolt” mód: szerviz-mentes oldalváltozatok hirdetési platformokhoz ─────
//
// A tulajdonos kérése (2026-11-03): a /telefonok, /felvasarlas és /uzletek
// oldalakból legyen egy változat, amelyben SEHOL nincs szerviz-utalás (menü,
// lábléc, szöveg, meta, strukturált adat) – hirdetési fiókhoz / platformhoz.
//
// Nem kódmásolat: UGYANAZOK az oldalak futnak a /bolt/... útvonalon, és ebben a
// módban elrejtik a szerviz-részeket. Így a készlet, az árak és a kalkulátor nem
// tud elcsúszni a két változat között.
//
// Szabályok bolt módban:
//   - minden belső link a /bolt/ oldalak között marad (boltHref); ami nem
//     képezhető le (szerviz, hidrogél, blog, VIP …), az NEM jelenik meg,
//   - az oldalak noindex-esek (a Google-ben az eredeti oldalak szerepelnek),
//   - a jogi oldalak (adatkezelés, ÁSZF, impresszum, cookie) linkje marad.
//
// Ellenőrzés: scripts/validate-build.js a /bolt/ oldalak renderelt szövegében,
// meta-adataiban és JSON-LD-jében szerviz-szavakat keres → build-hiba.
import { useLocation } from 'react-router-dom';

export const BOLT_PREFIX = '/bolt';

// Az eredeti útvonal → a bolt-változat. Csak ezek léteznek bolt módban.
const BOLT_UTAK = ['/telefonok', '/felvasarlas', '/uzletek'];
// Bolt módban is megtartható, semleges oldalak (jogi tájékoztatók).
const SEMLEGES = ['/adatkezeles', '/aszf', '/impresszum', '/cookie', '/akadalymentesseg'];

export const isBoltPath = (pathname = '') =>
  pathname === BOLT_PREFIX || pathname.startsWith(`${BOLT_PREFIX}/`);

export const useBoltMod = () => isBoltPath(useLocation().pathname);

/**
 * Egy belső link bolt-módbeli megfelelője.
 *   '/telefonok/?kategoria=iphone' → '/bolt/telefonok/?kategoria=iphone'
 *   '/telefonok/1234/'            → '/bolt/telefonok/1234/'
 *   '/aszf/'                      → '/aszf/' (semleges, marad)
 *   '/szerviz/'                   → null  (bolt módban nem jelenhet meg)
 * Külső (http, tel:, mailto:) és horgony (#) link változatlan.
 */
export const boltHref = (href) => {
  if (typeof href !== 'string' || !href.startsWith('/') || href.startsWith('//')) return href;
  if (isBoltPath(href)) return href;
  const ut = href.split(/[?#]/)[0].replace(/\/$/, '') || '/';
  if (BOLT_UTAK.some((b) => ut === b || ut.startsWith(`${b}/`))) return `${BOLT_PREFIX}${href}`;
  if (SEMLEGES.includes(ut)) return href;
  return null;
};

/** Hook: bolt módban leképez (null = rejtsd el), különben változatlan. */
export const useLinkMapper = () => {
  const bolt = useBoltMod();
  return bolt ? boltHref : (href) => href;
};

// ── Szerviz-szavak: EGY lista, ezt használja a meta-védőháló és a build-ellenőrzés
// (scripts/validate-build.js – ott ugyanez a minta, CommonJS-ben duplikálva).
export const SZERVIZ_MINTA = /szerv[ií]z|jav[ií]t|kijelz[őo]\s*-?\s*cser|akkumul[áa]tor\s*-?\s*cser|akku\s*-?\s*cser|h[áa]tlap|felhelyez|repair/i;

// Bolt-oldalankénti meta (cím + leírás). A termékoldal a saját címét tartja meg,
// ha az szerviz-mentes; különben ez a tartalék.
export const BOLT_META = {
  '/bolt/telefonok': {
    title: 'Használt telefonok Orosházán – bevizsgálva, garanciával | NorbiPhone',
    description: 'Használt iPhone, Samsung, Xiaomi és más telefonok két orosházi üzletben. Valós készlet, saját fotó, garancia és számla – átvétel előtt kipróbálhatod.',
  },
  '/bolt/felvasarlas': {
    title: 'Telefon felvásárlás, beszámítás Orosházán – készpénz | NorbiPhone',
    description: 'Mennyit ér a régi telefonod? Beszámítjuk egy újabb készülékbe, vagy készpénzben kifizetjük. Két üzlet Orosházán.',
  },
  '/bolt/uzletek': {
    title: 'Üzleteink Orosházán – Belváros és Tesco | NorbiPhone',
    description: 'A NorbiPhone két orosházi üzlete: Rákóczi út 16 és Vásárhelyi út 23/A (Tesco üzletsor). Címek, nyitvatartás, telefonszámok.',
  },
  termek: {
    title: 'Használt telefon Orosházán | NorbiPhone',
    description: 'Használt telefon a NorbiPhone orosházi készletéből – saját fotó, garancia és számla. Átvétel előtt kipróbálhatod.',
  },
};

/** A bolt-oldal meta-adata a pathname alapján. */
export const boltMeta = (pathname, title, description) => {
  const ut = pathname.replace(/\/$/, '');
  const alap = BOLT_META[ut] || BOLT_META.termek;
  const t = BOLT_META[ut] ? alap.title : (title && !SZERVIZ_MINTA.test(title) ? title : alap.title);
  const d = BOLT_META[ut] ? alap.description : (description && !SZERVIZ_MINTA.test(description) ? description : alap.description);
  return { title: t, description: d };
};

/** Általános szövegtisztítás bolt módban (csak a biztonságos, jelentéstartó
 *  cserék): „szervizgarancia” → „garancia” (minden ragozott alakban). A
 *  szervizre utaló MONDATOKAT az oldalak maguk cserélik le bolt-változatra. */
export const boltSzoveg = (szoveg) => (typeof szoveg === 'string'
  ? szoveg.replace(/Szervizgaranci/g, 'Garanci').replace(/szervizgaranci/g, 'garanci')
    .replace(/ a saját szervizünkben/g, '')
  : szoveg);

/** Egy hookban minden, ami egy komponensnek kell:
 *  bolt (bool), t (szövegtisztító), h (link-leképező – null = ne jelenjen meg). */
export const useBolt = () => {
  const bolt = useBoltMod();
  return {
    bolt,
    t: bolt ? boltSzoveg : (x) => x,
    h: bolt ? boltHref : (x) => x,
  };
};
